// utils/printWindow.js
// Ouvre la fenêtre d'impression thermique dans un contexte top-level.
//
// Wix embarque l'app dans une iframe dont l'attribut `allow` ne délègue pas
// `local-network-access` / `loopback-network` (Shopify, lui, les délègue). Toute
// connexion vers localhost — dont la websocket QZ Tray — y est donc interdite par
// la Permissions-Policy, sans que l'utilisateur puisse l'autoriser. Une fenêtre
// ouverte via window.open est un contexte top-level : elle n'hérite pas de cette
// restriction et peut parler à QZ Tray.
//
// La fenêtre doit être ouverte SYNCHRONEMENT dans le handler du clic, avant tout
// await : sans activation utilisateur, le bloqueur de popups l'intercepte. D'où le
// découpage en deux temps — openPrintWindow() au clic, puis send(job) une fois la
// liste des étiquettes connue.

const PRINT_WINDOW_URL = '/print-window';

/* Onglet, pas fenêtre flottante : window.open sans dimensions ouvre un onglet. Les deux
   sont soumis à la même règle d'activation utilisateur — l'onglet n'est qu'un choix
   visuel, il ne permet pas d'ouvrir plus tard. Le nom fixe garantit qu'un second clic
   réutilise l'onglet existant au lieu d'en empiler un nouveau. */
const WINDOW_NAME = 'colissimo_print_window';

const FROM_OPENER = 'colissimo-print/opener';
const FROM_WINDOW = 'colissimo-print/window';

// Code d'erreur émis quand l'utilisateur ferme la fenêtre lui-même : ce n'est pas
// un échec d'impression, les appelants ne doivent pas afficher d'erreur.
export const PRINT_WINDOW_CLOSED = 'PRINT_WINDOW_CLOSED';

/**
 * @returns {null|{ promise: Promise, send: Function, cancel: Function, window: Window }}
 *          null si la popup est bloquée par le navigateur.
 */
export function openPrintWindow() {
  /* La fenêtre est top-level : ses cookies sont first-party, elle ne partage donc pas
     la session de l'iframe (cookies tiers partitionnés) et ne connaît pas sa locale.
     On la lui passe explicitement, sinon Rails retombe sur la langue par défaut. */
  let url = PRINT_WINDOW_URL;
  try {
    const locale = window.I18n && window.I18n.locale;
    if (locale) url += '?locale=' + encodeURIComponent(String(locale).slice(0, 2));
  } catch (e) {}

  const win = window.open(url, WINDOW_NAME);
  if (!win) return null;

  const origin = window.location.origin;

  let ready = false;
  let pendingJob = null;
  let settled = false;
  let resolve;
  let reject;

  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
  // Marque la promesse comme gérée : un appelant qui ignore le résultat ne doit pas
  // provoquer d'unhandled rejection. Un .catch() ultérieur reste possible.
  promise.catch(() => {});

  const postToWindow = (message) => {
    try { win.postMessage(Object.assign({ source: FROM_OPENER }, message), origin); } catch (e) {}
  };

  const settle = (fn, value) => {
    if (settled) return;
    settled = true;
    window.removeEventListener('message', onMessage);
    clearInterval(closedWatcher);
    fn(value);
  };

  const onMessage = (event) => {
    if (event.origin !== origin) return;
    const data = event.data;
    if (!data || data.source !== FROM_WINDOW) return;

    if (data.type === 'READY') {
      ready = true;
      if (pendingJob) {
        postToWindow({ type: 'JOB', job: pendingJob });
      } else {
        /* Cas « générer puis imprimer » : la fenêtre est ouverte au clic mais les
           étiquettes n'existent pas encore. On lui dit d'attendre, sinon son délai
           de garde expire pendant la génération (qui peut durer plusieurs minutes
           sur un gros lot). */
        postToWindow({ type: 'WAIT' });
      }
    } else if (data.type === 'DONE') {
      settle(resolve, { printed: data.printed || 0 });
    } else if (data.type === 'ERROR') {
      const error = new Error(data.message || 'print window error');
      error.code = data.code || 'PRINT_WINDOW_ERROR';
      settle(reject, error);
    }
  };

  const closedWatcher = setInterval(() => {
    if (!win.closed) return;
    const error = new Error('print window closed');
    error.code = PRINT_WINDOW_CLOSED;
    settle(reject, error);
  }, 500);

  window.addEventListener('message', onMessage);

  return {
    window: win,
    promise: promise,

    /** job = { protocole: string, requests: string[] } — requests = endpoints backend
     *  renvoyant { printer_request } ou { request_urls: [] }. */
    send: (job) => {
      pendingJob = job;
      if (ready) postToWindow({ type: 'JOB', job: job });
    },

    /** Abandonne la tâche (ex. : la vérification en amont a échoué) et ferme la fenêtre. */
    cancel: (message) => {
      postToWindow({ type: 'CANCEL', message: message || null });
      settle(resolve, { printed: 0, cancelled: true });
    },
  };
}
